import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/hooks/ui/use-toast";
import Navbar from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Building, 
  MapPin, 
  Phone, 
  Globe, 
  Save, 
  ArrowLeft, 
  Settings,
  Clock,
  Users,
  Trash2,
  Edit3,
  CheckCircle,
  AlertCircle
} from "lucide-react";

const businessSchema = z.object({
  name: z.string().min(2, "Business name must be at least 2 characters"),
  description: z.string().optional(),
  address: z.string().min(5, "Please enter a valid address"),
  website: z.string().url("Please enter a valid website URL").optional().or(z.literal("")),
  phone: z.string().min(5, "Please enter a valid phone number").optional().or(z.literal("")),
});

type BusinessFormValues = z.infer<typeof businessSchema>;

interface Business {
  id: string;
  name: string;
  description: string | null;
  address: string;
  website: string | null;
  phone: string | null;
  google_place_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
}

interface BusinessSettings {
  enable_reviews: boolean;
  enable_loyalty: boolean;
  enable_shifts: boolean;
  enable_payroll: boolean;
  auto_responses: boolean;
  notification_email: string;
}

const EditBusiness = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [business, setBusiness] = useState<Business | null>(null);
  const [businessSettings, setBusinessSettings] = useState<BusinessSettings>({
    enable_reviews: true,
    enable_loyalty: true,
    enable_shifts: true,
    enable_payroll: true,
    auto_responses: false,
    notification_email: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("details");

  const form = useForm<BusinessFormValues>({
    resolver: zodResolver(businessSchema),
    defaultValues: {
      name: "",
      description: "",
      address: "",
      website: "",
      phone: "",
    },
  });

  useEffect(() => {
    if (!user) {
      toast.error("Authentication Required", {
        description: "You must be logged in to edit business details."
      });
      navigate("/sign-in");
      return;
    }

    fetchBusinessDetails();
  }, [businessId, user, navigate]);

  const fetchBusinessDetails = async () => {
    if (!businessId) return;

    try {
      setLoading(true);
      
      // Fetch business details
      const { data: businessData, error: businessError } = await supabase
        .from("businesses")
        .select("*")
        .eq("id", businessId)
        .single();

      if (businessError) throw businessError;
      
      setBusiness(businessData as Business);
      
      // Update form with current business data
      form.reset({
        name: businessData.name,
        description: businessData.description || "",
        address: businessData.address,
        website: businessData.website || "",
        phone: businessData.phone || "",
      });

      // Fetch business settings (if they exist)
      const { data: settingsData } = await supabase
        .from("business_settings")
        .select("*")
        .eq("business_id", businessId)
        .single();

      if (settingsData) {
        setBusinessSettings({
          enable_reviews: settingsData.enable_reviews ?? true,
          enable_loyalty: settingsData.enable_loyalty ?? true,
          enable_shifts: settingsData.enable_shifts ?? true,
          enable_payroll: settingsData.enable_payroll ?? true,
          auto_responses: settingsData.auto_responses ?? false,
          notification_email: settingsData.notification_email || "",
        });
      }

    } catch (error) {
      console.error("Error fetching business details:", error);
      toast.error("Error", {
        description: "Failed to load business details. Please try again."
      });
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (values: BusinessFormValues) => {
    if (!business) return;

    try {
      setSaving(true);

      const { error } = await supabase
        .from("businesses")
        .update({
          name: values.name,
          description: values.description || null,
          address: values.address,
          website: values.website || null,
          phone: values.phone || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", business.id);

      if (error) throw error;

      // Update business settings
      const { error: settingsError } = await supabase
        .from("business_settings")
        .upsert({
          business_id: business.id,
          ...businessSettings,
          updated_at: new Date().toISOString(),
        });

      if (settingsError) throw settingsError;

      toast.success("Success", {
        description: "Business details updated successfully."
      });

      // Refresh business data
      fetchBusinessDetails();

    } catch (error) {
      console.error("Error updating business:", error);
      toast.error("Error", {
        description: "Failed to update business details. Please try again."
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBusiness = async () => {
    if (!business) return;

    try {
      setSaving(true);

      // Soft delete - set is_active to false
      const { error } = await supabase
        .from("businesses")
        .update({
          is_active: false,
          updated_at: new Date().toISOString(),
        })
        .eq("id", business.id);

      if (error) throw error;

      toast.success("Business Deactivated", {
        description: "Your business has been deactivated. You can reactivate it anytime."
      });

      navigate("/dashboard");

    } catch (error) {
      console.error("Error deactivating business:", error);
      toast.error("Error", {
        description: "Failed to deactivate business. Please try again."
      });
    } finally {
      setSaving(false);
      setDeleteDialogOpen(false);
    }
  };

  const updateBusinessSetting = (key: keyof BusinessSettings, value: boolean | string) => {
    setBusinessSettings(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-12 flex justify-center items-center">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
            <p>Loading business details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-12">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
            <h1 className="text-2xl font-bold mb-4">Business Not Found</h1>
            <p className="mb-6 text-gray-600">
              The business you're trying to edit doesn't exist or you don't have access to it.
            </p>
            <Button onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(`/business/${business.id}`)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Business Details
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <Edit3 className="w-8 h-8" />
                Edit Business
              </h1>
              <p className="text-gray-600 mt-1">Manage your business information and settings</p>
            </div>
            <Badge variant={business.is_active ? "default" : "secondary"}>
              {business.is_active ? "Active" : "Inactive"}
            </Badge>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details" className="flex items-center gap-2">
              <Building className="w-4 h-4" />
              Business Details
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="advanced" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Advanced
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  Business Information
                </CardTitle>
                <CardDescription>
                  Update your business details and contact information
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Business Name *</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter business name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone Number</FormLabel>
                            <FormControl>
                              <div className="flex items-center">
                                <Phone className="w-4 h-4 text-gray-400 mr-2" />
                                <Input placeholder="+1 (555) 123-4567" {...field} />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Describe your business"
                              className="min-h-[100px]"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            This will be visible to customers and help them understand your business
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Address *</FormLabel>
                          <FormControl>
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                              <Input placeholder="123 Main St, City, State 12345" {...field} />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="website"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Website</FormLabel>
                          <FormControl>
                            <div className="flex items-center">
                              <Globe className="w-4 h-4 text-gray-400 mr-2" />
                              <Input placeholder="https://www.example.com" {...field} />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex justify-end space-x-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate(`/business/${business.id}`)}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" disabled={saving}>
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? "Saving..." : "Save Changes"}
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Feature Settings
                </CardTitle>
                <CardDescription>
                  Enable or disable features for your business
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base">Review Management</Label>
                      <div className="text-sm text-gray-500">
                        Collect and manage customer reviews
                      </div>
                    </div>
                    <Switch
                      checked={businessSettings.enable_reviews}
                      onCheckedChange={(checked) => updateBusinessSetting('enable_reviews', checked)}
                    />
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base">Loyalty Programs</Label>
                      <div className="text-sm text-gray-500">
                        Create digital loyalty cards and rewards
                      </div>
                    </div>
                    <Switch
                      checked={businessSettings.enable_loyalty}
                      onCheckedChange={(checked) => updateBusinessSetting('enable_loyalty', checked)}
                    />
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base">Shift Management</Label>
                      <div className="text-sm text-gray-500">
                        Schedule and track employee shifts
                      </div>
                    </div>
                    <Switch
                      checked={businessSettings.enable_shifts}
                      onCheckedChange={(checked) => updateBusinessSetting('enable_shifts', checked)}
                    />
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base">Payroll Management</Label>
                      <div className="text-sm text-gray-500">
                        Track time and manage payroll
                      </div>
                    </div>
                    <Switch
                      checked={businessSettings.enable_payroll}
                      onCheckedChange={(checked) => updateBusinessSetting('enable_payroll', checked)}
                    />
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base">Automated Responses</Label>
                      <div className="text-sm text-gray-500">
                        Automatically respond to customer reviews
                      </div>
                    </div>
                    <Switch
                      checked={businessSettings.auto_responses}
                      onCheckedChange={(checked) => updateBusinessSetting('auto_responses', checked)}
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <Label htmlFor="notification-email">Notification Email</Label>
                  <Input
                    id="notification-email"
                    type="email"
                    placeholder="notifications@example.com"
                    value={businessSettings.notification_email}
                    onChange={(e) => updateBusinessSetting('notification_email', e.target.value)}
                    className="mt-2"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Important business notifications will be sent to this email
                  </p>
                </div>

                <div className="flex justify-end">
                  <Button onClick={() => onSubmit(form.getValues())} disabled={saving}>
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? "Saving..." : "Save Settings"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="advanced" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="w-5 h-5" />
                  Danger Zone
                </CardTitle>
                <CardDescription>
                  Irreversible actions that affect your business
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-red-800">Deactivate Business</h3>
                      <p className="text-sm text-red-600 mt-1">
                        This will deactivate your business but preserve all data. 
                        You can reactivate it anytime from your dashboard.
                      </p>
                    </div>
                    <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Deactivate
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will deactivate "{business.name}" and make it inaccessible to customers.
                            All data will be preserved and you can reactivate it anytime.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleDeleteBusiness}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Yes, Deactivate Business
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EditBusiness;